import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { CandlestickSeries, LineSeries } from 'lightweight-charts';
import { sanitizeListingPriceBars } from '../utils/etpChartBars';
import { mountListingChartWhenSized } from '../utils/listingPriceChartMount';
import { computeSmaLineData } from '../utils/stockMaChartData';
import {
  applyVisibleRange,
  DEFAULT_ZOOM,
  flattenCommodityEtcs,
  fmtBucket,
  fmtCostPct,
  fmtPrice,
  fmtReplication,
  fmtSpreadPct,
  syntheticRtc,
  INITIAL_SMA_ACTIVE,
  REPLICATION_TITLES,
  rowKey,
  SMA_DEFS,
  ZOOM_RANGES,
} from '../utils/etpListingHelpers';
import EtcCotBucketPanel from './EtcCotBucketPanel';
import EtcRollPanel from './EtcRollPanel';
import './InvestingPage.css';
import './EtpPricesPage.css';

const ISSUER_FILTERS = [
  { id: 'all', label: 'All issuers' },
  { id: 'Amundi ETPs', label: 'Amundi' },
  { id: 'WisdomTree ETPs', label: 'WisdomTree' },
  { id: 'Basket ETPs', label: 'Strategic basket' },
];

const REPLICATION_FILTERS = [
  { id: 'all', label: 'All replication' },
  { id: 'physical', label: 'Physical' },
  { id: 'futures', label: 'Futures' },
  { id: 'synthetic', label: 'Synthetic' },
];

export default function EtcCotPage() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const smaSeriesRef = useRef({});

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [issuerFilter, setIssuerFilter] = useState('all');
  const [replicationFilter, setReplicationFilter] = useState('all');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [bars, setBars] = useState([]);
  const [zoomRange, setZoomRange] = useState(DEFAULT_ZOOM);
  const zoomRangeRef = useRef(DEFAULT_ZOOM);
  const barsRef = useRef([]);
  const [meta, setMeta] = useState(null);
  const [smaActive, setSmaActive] = useState(INITIAL_SMA_ACTIVE);
  const [layoutEpoch, setLayoutEpoch] = useState(0);

  useEffect(() => {
    zoomRangeRef.current = zoomRange;
  }, [zoomRange]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/investing/symbols');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (cancelled) return;
        const symbols = flattenCommodityEtcs(data.groups);
        setRows(symbols);
        setMeta({ prices_fetched_at: data.prices_fetched_at });
        if (symbols.length === 0) {
          throw new Error('No commodity ETC listings in DB');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load ETC symbols');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (issuerFilter !== 'all') {
      next = next.filter((row) => row.sector_group === issuerFilter);
    }
    if (replicationFilter !== 'all') {
      next = next.filter((row) => row.replication === replicationFilter);
    }
    if (bucketFilter !== 'all') {
      next = next.filter((row) => row.bucket === bucketFilter);
    }
    return next.sort((a, b) => {
      const ba = a.bucket || '';
      const bb = b.bucket || '';
      if (ba !== bb) return ba.localeCompare(bb);
      return (a.symbol || '').localeCompare(b.symbol || '');
    });
  }, [rows, issuerFilter, replicationFilter, bucketFilter]);

  useEffect(() => {
    if (filteredRows.length === 0) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow((prev) => {
      if (prev && filteredRows.some((row) => rowKey(row) === rowKey(prev))) {
        return prev;
      }
      return filteredRows[0];
    });
  }, [filteredRows]);

  useEffect(() => {
    barsRef.current = bars;
  }, [bars]);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!selectedRow || !el) return undefined;

    const dispose = mountListingChartWhenSized(
      el,
      {
        layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
        grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
        crosshair: { mode: 1 },
        rightPriceScale: { borderColor: '#E3D9C6' },
        timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
        handleScroll: {
          mouseWheel: true,
          pressedMouseMove: true,
          horzTouchDrag: true,
          vertTouchDrag: false,
        },
        handleScale: {
          axisPressedMouseMove: true,
          axisDoubleClickReset: true,
          mouseWheel: true,
          pinch: true,
        },
      },
      (chart) => {
        const candleSeries = chart.addSeries(CandlestickSeries, {
          upColor: '#1F4E8C',
          downColor: '#C0571F',
          borderVisible: false,
          wickUpColor: '#1F4E8C',
          wickDownColor: '#C0571F',
        });

        const smaSeries = {};
        SMA_DEFS.forEach((def) => {
          smaSeries[def.key] = chart.addSeries(LineSeries, {
            color: def.color,
            lineWidth: def.lineWidth,
            visible: false,
            priceLineVisible: false,
            lastValueVisible: false,
          });
        });

        chartRef.current = chart;
        candleSeriesRef.current = candleSeries;
        smaSeriesRef.current = smaSeries;

        const currentBars = barsRef.current;
        if (currentBars.length) {
          candleSeries.setData(currentBars);
          applyVisibleRange(chart, currentBars, zoomRangeRef.current);
        }
      },
    );

    return () => {
      dispose();
      chartRef.current = null;
      candleSeriesRef.current = null;
      smaSeriesRef.current = {};
    };
  }, [selectedRow?.symbol]);

  useEffect(() => {
    const el = chartContainerRef.current;
    const chart = chartRef.current;
    if (!el || !chart) return;
    const height = Math.max(el.clientHeight, el.parentElement?.clientHeight ?? 0, 200);
    const width = el.clientWidth;
    if (width >= 8 && height >= 8) {
      chart.applyOptions({ width, height });
      if (barsRef.current.length) {
        applyVisibleRange(chart, barsRef.current, zoomRangeRef.current);
      }
    }
  }, [layoutEpoch]);

  useEffect(() => {
    if (!bars.length || !candleSeriesRef.current) return;
    candleSeriesRef.current.setData(bars);
    applyVisibleRange(chartRef.current, bars, zoomRangeRef.current);
  }, [bars]);

  useEffect(() => {
    const seriesMap = smaSeriesRef.current;
    if (!bars.length || !Object.keys(seriesMap).length) return;
    SMA_DEFS.forEach((def) => {
      const series = seriesMap[def.key];
      if (!series) return;
      if (smaActive[def.key]) {
        series.setData(computeSmaLineData(bars, def.period));
        series.applyOptions({ visible: true });
      } else {
        series.applyOptions({ visible: false });
      }
    });
  }, [bars, smaActive]);

  useEffect(() => {
    if (!selectedRow) {
      setBars([]);
      setChartError(null);
      candleSeriesRef.current?.setData([]);
      return;
    }
    let cancelled = false;
    async function loadChartData() {
      setChartLoading(true);
      setChartError(null);
      try {
        const symbol = encodeURIComponent(selectedRow.symbol);
        const priceResponse = await fetch(`/api/investing/prices/${symbol}`);
        if (!priceResponse.ok) throw new Error(`Prices HTTP ${priceResponse.status}`);
        const priceData = await priceResponse.json();
        if (cancelled) return;
        const anchor = selectedRow.close ?? null;
        const nextBars = sanitizeListingPriceBars(priceData.bars || [], anchor);
        setBars(nextBars);
        candleSeriesRef.current?.setData(nextBars);
        applyVisibleRange(chartRef.current, nextBars, zoomRangeRef.current);
      } catch (err) {
        if (!cancelled) {
          setChartError(err.message || 'Failed to load price history');
          setBars([]);
          candleSeriesRef.current?.setData([]);
        }
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    }
    loadChartData();
    return () => { cancelled = true; };
  }, [selectedRow]);

  const handleZoomChange = useCallback((rangeLabel) => {
    zoomRangeRef.current = rangeLabel;
    setZoomRange(rangeLabel);
    applyVisibleRange(chartRef.current, bars, rangeLabel);
  }, [bars]);

  const toggleSma = useCallback((key) => {
    setSmaActive((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleBucketFromCot = useCallback((bucket) => {
    setBucketFilter((prev) => (prev === bucket ? 'all' : bucket));
  }, []);

  const handleRollLayoutChange = useCallback(() => {
    setLayoutEpoch((n) => n + 1);
  }, []);

  const latestBar = bars.length > 0 ? bars[bars.length - 1] : null;
  const displayClose = latestBar?.close ?? selectedRow?.close ?? null;
  const displayDate = latestBar?.time ?? selectedRow?.price_date ?? null;

  return (
    <div className="etc-cot-page etp-prices-page investing-page page-content">
      <header className="etp-prices-header etc-cot-section-full">
        <div>
          <h2 className="etp-prices-title">ETC</h2>
          <p className="etp-prices-subtitle">
            Commodity ETC listings, daily OHLCV, and CFTC bucket positioning.
            {meta?.prices_fetched_at && (
              <span className="etp-prices-meta">
                {' '}
                Prices updated {meta.prices_fetched_at}
              </span>
            )}
          </p>
        </div>
        <div className="etp-header-filters">
          <nav className="etp-type-tabs" aria-label="ETC issuer">
            {ISSUER_FILTERS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`etp-type-tab${issuerFilter === tab.id ? ' active' : ''}`}
                onClick={() => setIssuerFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          <nav className="etp-type-tabs" aria-label="ETC replication">
            {REPLICATION_FILTERS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`etp-type-tab etp-repl-tab${replicationFilter === tab.id ? ' active' : ''}`}
                onClick={() => setReplicationFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          {bucketFilter !== 'all' && (
            <button
              type="button"
              className="etp-type-tab active"
              onClick={() => setBucketFilter('all')}
              title="Clear bucket filter from COT table"
            >
              Bucket: {fmtBucket(bucketFilter)} ×
            </button>
          )}
        </div>
      </header>

      <div className="investing-left etc-cot-left-column">
        <div className="etc-cot-listings-table-block">
        <div className="investing-controls">
          <span className="investing-count">
            {loading ? 'Loading…' : `${filteredRows.length} ETC listings`}
          </span>
        </div>
        {error && (
          <div className="investing-error">
            {error}. Is investing_backend running on port 5001?
          </div>
        )}
        <div className="investing-table-wrap">
          <table className="investing-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Name</th>
                <th>Issuer</th>
                <th>Bucket</th>
                <th className="investing-th-filter">
                  <span className="investing-th-filter-label">Replication</span>
                  <select
                    className="investing-th-select"
                    aria-label="Filter by replication"
                    value={replicationFilter}
                    onChange={(e) => setReplicationFilter(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {REPLICATION_FILTERS.map(({ id, label }) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </select>
                </th>
                <th>Last</th>
                <th>TER</th>
                <th>As of</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="investing-empty">No ETC listings match.</td>
                </tr>
              )}
              {filteredRows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={
                    selectedRow && rowKey(selectedRow) === rowKey(row)
                      ? 'investing-row-selected'
                      : ''
                  }
                  onClick={() => {
                    zoomRangeRef.current = DEFAULT_ZOOM;
                    setZoomRange(DEFAULT_ZOOM);
                    setSelectedRow(row);
                  }}
                >
                  <td className="investing-symbol">{row.symbol}</td>
                  <td>{row.name || '—'}</td>
                  <td>{row.issuer || '—'}</td>
                  <td className="etp-bucket-cell">{fmtBucket(row.bucket)}</td>
                  <td>
                    {row.replication ? (
                      <span
                        className={`etp-repl-badge etp-repl-${row.replication}`}
                        title={REPLICATION_TITLES[row.replication] || ''}
                      >
                        {fmtReplication(row.replication)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="investing-mono">{fmtPrice(row.close)}</td>
                  <td className="investing-mono">{fmtCostPct(row.ter)}</td>
                  <td className="investing-mono">{row.price_date || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      <div className="investing-right etc-cot-right-column">
        {!selectedRow && (
          <div className="investing-placeholder etc-cot-chart-placeholder">
            <span>Select an ETC to view OHLC and roll yield</span>
          </div>
        )}

        {selectedRow && (
          <section
            className="etp-price-dashboard etc-cot-price-primary"
            aria-label="ETC price chart"
          >
            <div className="investing-chart-header etc-cot-chart-header">
              <div className="investing-chart-title">
                <span className="investing-chart-symbol">{selectedRow.symbol}</span>
                <span className="investing-chart-name">{selectedRow.name || '—'}</span>
                {selectedRow.bucket && (
                  <span className="etp-bucket-badge">{fmtBucket(selectedRow.bucket)}</span>
                )}
                {selectedRow.replication && (
                  <span className={`etp-repl-badge etp-repl-${selectedRow.replication}`}>
                    {fmtReplication(selectedRow.replication)}
                  </span>
                )}
              </div>
              <dl className="etp-selected-costs" aria-label="Listing costs">
                <div className="etp-selected-cost-item">
                  <dt>ISIN</dt>
                  <dd className="etp-selected-cost-isin">{selectedRow.isin || '—'}</dd>
                </div>
                <div className="etp-selected-cost-item">
                  <dt>TER</dt>
                  <dd>{fmtCostPct(selectedRow.ter)}</dd>
                </div>
                <div className="etp-selected-cost-item">
                  <dt>Spread</dt>
                  <dd title={selectedRow.bid != null && selectedRow.ask != null ? `bid ${selectedRow.bid} / ask ${selectedRow.ask}` : ''}>
                    {fmtSpreadPct(selectedRow.spread_relative)}
                  </dd>
                </div>
                <div className="etp-selected-cost-item">
                  <dt>RTC</dt>
                  <dd>
                    {fmtCostPct(
                      selectedRow.rtc ??
                        selectedRow.cs ??
                        syntheticRtc(selectedRow.ter, selectedRow.spread_relative),
                    )}
                  </dd>
                </div>
              </dl>
              <div className="investing-chart-meta">
                {displayClose != null && (
                  <span className="investing-chart-price">
                    {fmtPrice(displayClose)}
                    {displayDate && (
                      <span className="investing-chart-date"> · {displayDate}</span>
                    )}
                  </span>
                )}
                {bars.length > 0 && (
                  <span className="investing-chart-bars">{bars.length} days</span>
                )}
              </div>
              <div className="investing-zoom-toolbar">
                <span className="investing-zoom-label">Range</span>
                {ZOOM_RANGES.map(({ label }) => (
                  <button
                    key={label}
                    type="button"
                    className={`investing-zoom-btn${zoomRange === label ? ' active' : ''}`}
                    onClick={() => handleZoomChange(label)}
                    disabled={chartLoading || bars.length === 0}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="etp-sma-toolbar" aria-label="Simple moving averages">
                <span className="investing-zoom-label">SMA</span>
                {SMA_DEFS.map((def) => (
                  <label
                    key={def.key}
                    className={`etp-sma-toggle${smaActive[def.key] ? ' active' : ''}`}
                    style={{ '--sma-color': def.color }}
                  >
                    <input
                      type="checkbox"
                      checked={!!smaActive[def.key]}
                      onChange={() => toggleSma(def.key)}
                      disabled={chartLoading || bars.length === 0}
                    />
                    <span className="etp-sma-dot" aria-hidden />
                    {def.label}
                  </label>
                ))}
              </div>
            </div>
            {chartError && (
              <div className="investing-error">
                Could not load prices for {selectedRow.symbol}: {chartError}
              </div>
            )}
            {chartLoading && (
              <div className="investing-chart-loading">Loading chart…</div>
            )}
            <div className="investing-charts-area etc-cot-charts-area">
              <div ref={chartContainerRef} className="investing-chart-wrap etc-cot-chart-wrap" />
            </div>
          </section>
        )}

        <div className="etc-cot-detail-scroll">
          {selectedRow && (
            <EtcRollPanel
              className="etc-cot-roll-above-cot"
              symbol={selectedRow.symbol}
              replication={selectedRow.replication}
              category={selectedRow.category}
              onLayoutChange={handleRollLayoutChange}
              hideIfUnavailable
            />
          )}
          <div className="etc-cot-buckets-wrap etc-cot-buckets-compact">
            <EtcCotBucketPanel
              selectedBucket={bucketFilter === 'all' ? null : bucketFilter}
              onBucketSelect={handleBucketFromCot}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
